import { useState, useEffect, useCallback } from 'react';
import { docsApi } from '@/services/docsApi';
import { workflowApi } from '@/services/workflowApi';
import { useWorkspace } from '@/contexts/WorkspaceContext';

interface UseClaudeConfigResult {
  content: string;
  setContent: (content: string) => void;
  loading: boolean;
  error: string | null;
  exists: boolean;
  save: (commitMessage?: string) => Promise<void>;
  saving: boolean;
  saved: boolean;
  reload: () => void;
}

export function useClaudeConfig(path: string): UseClaudeConfigResult {
  const { selectedRepo } = useWorkspace();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exists, setExists] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setContent('');
    setError(null);
    setExists(false);
    setLoading(true);
    setSaved(false);

    if (!selectedRepo) {
      setLoading(false);
      return;
    }

    const [owner, repo] = selectedRepo.split('/');
    docsApi
      .getFileContent(owner, repo, path)
      .then((result) => {
        if (result.content) {
          setContent(result.content);
          setExists(true);
        }
      })
      .catch(() => {
        // File doesn't exist — that's fine
      })
      .finally(() => setLoading(false));
  }, [selectedRepo, path, reloadKey]);

  const save = useCallback(
    async (commitMessage?: string) => {
      if (!selectedRepo || !content) return;
      setSaving(true);
      setSaved(false);
      try {
        await workflowApi.publishFile(selectedRepo, path, content, commitMessage);
        setSaved(true);
        setExists(true);
        setTimeout(() => setSaved(false), 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [selectedRepo, path, content],
  );

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { content, setContent, loading, error, exists, save, saving, saved, reload };
}
